<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import yup from '@/plugins/yup'
import { ValidationError } from 'yup'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import router from '@/router'
import { useMaintenanceStore } from '@/store/maintenance'
import { ICreateMaintenance } from '@/interfaces/api/maintenance'
import { UserRoles } from '@/interfaces/enums'
import { storeToRefs } from 'pinia'
import { useSensorStore } from '@/store/sensor'
import { useCompanyStore } from '@/store/company'
import { useUserStore } from '@/store/user'

const { t } = useI18n()
const isLoading = ref(false)

const { createMaintenance } = useMaintenanceStore()
const { listMaintenanceTypes } = storeToRefs(useMaintenanceStore())
const { getMaintenanceTypes } = useMaintenanceStore()

const companyStore = useCompanyStore()
const { currentCompany, getCompanies, getAllCompanies, getCompanyUsers } = storeToRefs(companyStore)
const { fetchCompanyUsers, fetchCompanyUsersByCompanyId } = companyStore

const userStore = useUserStore()
const showAllocationSection = computed(() => {
  const role = userStore.getCurrentUser?.role as string | undefined
  return role === UserRoles.ADMIN || role === UserRoles.MANAGER
})

const sensorStore = useSensorStore()
const { listSensors, getSensorsForCompany } = storeToRefs(sensorStore)
const { fetch: fetchSensors, fetchSensorsByCompanyId } = sensorStore

const isSuperAdmin = computed(() => Boolean(currentCompany.value?.super_admin))

const companies = computed(() => {
  if (isSuperAdmin.value) {
    const clients = getAllCompanies.value?.length ? getAllCompanies.value : []
    const current = currentCompany.value
    if (current?.id != null && !clients.some((c) => c.id === current.id)) {
      return [current, ...clients]
    }
    return clients.length ? clients : getCompanies.value ?? []
  }
  return getCompanies.value ?? []
})

const selectedCompanyIdForForm = ref<number | null>(null)
const selectedAllocationCompanyId = ref<number | null>(null)

const sensorOptions = computed(() => {
  if (isSuperAdmin.value && selectedCompanyIdForForm.value != null) {
    return getSensorsForCompany.value(selectedCompanyIdForForm.value)
  }
  return listSensors.value
})

const selectedCompanyForDropdown = computed(() => {
  if (selectedCompanyIdForForm.value == null) return undefined
  return companies.value.find((c) => c.id === selectedCompanyIdForForm.value) ?? (currentCompany.value?.id === selectedCompanyIdForForm.value ? currentCompany.value : undefined)
})

const selectedAllocationCompanyForDropdown = computed(() => {
  if (selectedAllocationCompanyId.value == null) return undefined
  return companies.value.find((c) => c.id === selectedAllocationCompanyId.value) ?? (currentCompany.value?.id === selectedAllocationCompanyId.value ? currentCompany.value : undefined)
})

const priorityOptions = ref([
  { id: 'low', name: t('maintenance_listing.header.priority.low') },
  { id: 'medium', name: t('maintenance_listing.header.priority.medium') },
  { id: 'high', name: t('maintenance_listing.header.priority.high') },
])


function handleMaintenanceTypeSelected(option: any) {
  formData.value.maintenance_type_id = option.id
}

function handlePrioritySelected(option: any) {
  formData.value.priority = option.id
}

function handleSensorSelected(option: { id: string | number }) {
  formData.value.sensor_id = String(option.id)
}

function handleCompanySelected(option: { id: number }) {
  if (selectedCompanyIdForForm.value === option.id) return

  selectedCompanyIdForForm.value = option.id
  formData.value.company_id = option.id
  formData.value.sensor_id = ''
  fetchSensorsByCompanyId(option.id)
}

function handleAllocationCompanySelected(option: { id: number }) {
  if (selectedAllocationCompanyId.value === option.id) return

  selectedAllocationCompanyId.value = option.id
  formData.value.allocated_user_id = null
  
  fetchCompanyUsersByCompanyId(option.id)
}

function handleAllocatedUserSelected(option: { id: number }) {
  formData.value.allocated_user_id = option.id
}

const allocationUserOptions = computed(() => getCompanyUsers.value ?? [])

const selectedAllocatedUserForDropdown = computed(() => {
  if (formData.value.allocated_user_id == null) return undefined
  return allocationUserOptions.value.find((u: { id: number }) => u.id === formData.value.allocated_user_id) ?? undefined
})

const errors = ref({
  maintenance_type_id: '',
  description: '',
  priority: '',
  requires_shutdown: '',
  sensor_id: '',
  origin: '',
  status: '',
  company_id: '',
  allocated_user_id: '',
})

const formData = ref({
  maintenance_type_id: '',
  description: '',
  priority: '',
  requires_shutdown: false,
  sensor_id: '',
  origin: 'Manual',
  status: 'open',
  company_id: null as number | null,
  allocated_user_id: null as number | null,
})

const maintenanceSchema = yup.object({
  maintenance_type_id: yup.string().required(t('errors.required', { label: t('maintenance_listing.body.table.columns.maintenance_type') })),
  description: yup.string().min(3, t('errors.minLength', { label: t('maintenance_listing.body.table.columns.description'), min: '10' })),
  priority: yup.string().required(t('errors.required', { label: t('maintenance_listing.body.table.columns.priority') })),
  requires_shutdown: yup.boolean(),
  sensor_id: yup
    .string()
    .transform((v) => (v === '' || v == null ? undefined : v))
    .optional(),
  origin: yup.string().required(t('errors.required', { label: t('maintenance_listing.body.table.columns.origin') })),
})

function setErrors(validationErrors: ValidationError) {
  errors.value = {
    maintenance_type_id: '',
    description: '',
    priority: '',
    requires_shutdown: '',
    sensor_id: '',
    origin: '',
    status: '',
    company_id: '',
    allocated_user_id: '',
  }
  validationErrors.inner.forEach((error) => {
    if (error.path) {
      errors.value[error.path as keyof typeof errors.value] = error.message
    }
  })
}

function clearErrors() {
  errors.value = {
    maintenance_type_id: '',
    description: '',
    priority: '',
    requires_shutdown: '',
    sensor_id: '',
    origin: '',
    status: '',
    company_id: '',
    allocated_user_id: '',
  }
}

async function submitMaintenanceTypeForm() {
  const values: ICreateMaintenance = {
    maintenance_type_id: formData.value.maintenance_type_id,
    description: formData.value.description,
    priority: formData.value.priority,
    requires_shutdown: formData.value.requires_shutdown,
    origin: formData.value.origin,
    status: formData.value.status,
  }
  if (formData.value.sensor_id) {
    values.sensor_id = formData.value.sensor_id
  }
  if (showAllocationSection.value && formData.value.allocated_user_id != null) {
    values.allocated_user_id = formData.value.allocated_user_id
  }
  if (isSuperAdmin.value && selectedAllocationCompanyId.value != null) {
    values.allocated_company_id = selectedAllocationCompanyId.value
  }
  try {
    clearErrors()
    await maintenanceSchema.validate(values, { abortEarly: false })
    if (
      isSuperAdmin.value &&
      selectedAllocationCompanyId.value != null &&
      selectedAllocationCompanyId.value !== currentCompany.value?.id &&
      formData.value.allocated_user_id == null
    ) {
      errors.value.allocated_user_id = t('maintenance_create.body.form.allocated_user_required')
      return
    }
    isLoading.value = true
    await createMaintenance(values)
    isLoading.value = false
    router.push({ name: 'maintenance-listing' })
  } catch (err) {
    if (err instanceof ValidationError) {
      setErrors(err)
    }
  }
}

onMounted(() => {
  getMaintenanceTypes()
  if (isSuperAdmin.value) {
    companyStore.fetchAllCompanies()
    const companyId = currentCompany.value?.id ?? null
    selectedCompanyIdForForm.value = companyId
    formData.value.company_id = companyId
    if (companyId != null) {
      fetchSensorsByCompanyId(companyId)
    }
    if (showAllocationSection.value) {
      selectedAllocationCompanyId.value = companyId
      if (companyId != null) {
        fetchCompanyUsersByCompanyId(companyId)
      }
    }
  } else {
    fetchSensors()
    if (showAllocationSection.value) {
      fetchCompanyUsers()
    }
  }
})


const cancel = () => {
  router.back()
}
</script>

<template>
  <form @submit.prevent="submitMaintenanceTypeForm" class="space-y-6">
    <div>
      <h2 class="mb-4 border-b border-gray-300 pb-3 text-lg font-semibold">{{ t('maintenance_create.body.subtitle_basic_info') }}</h2>
      <div class="flex flex-wrap gap-4">
        <div class="w-full md:w-[48%]">
          <label class="mb-2.5 block font-medium text-black">{{ t('maintenance_create.body.form.maintenance_type') }}</label>
          <Dropdown
            v-model="formData.maintenance_type_id"
            name="maintenance_type"
            :label="t('maintenance_listing.body.table.columns.maintenance_type')"
            :options="listMaintenanceTypes"
            :placeholder="t('maintenance_create.header.actions.placeholder.maintenance_type')"
            :label-attribute="'name'"
            @option-selected="handleMaintenanceTypeSelected" />
          <p v-if="errors.maintenance_type_id" class="mt-1 text-sm text-red-500">
            {{ errors.maintenance_type_id }}
          </p>
        </div>

        <div class="w-full md:w-[48%]">
          <label class="mb-2.5 block font-medium text-black">{{ t('maintenance_create.body.form.origin') }}</label>
          <input v-model="formData.origin" type="text" disabled class="w-full border border-stroke bg-gray-100 px-4 py-2 text-black" />
          <p v-if="errors.origin" class="mt-1 text-sm text-red-500">
            {{ errors.origin }}
          </p>
        </div>

        <div class="w-full md:w-[48%]">
          <label class="mb-2.5 block font-medium text-black">{{ t('maintenance_create.body.form.priority') }}</label>
          <Dropdown
            v-model="formData.priority"
            name="priority"
            :label="t('maintenance_listing.body.table.columns.priority')"
            :options="priorityOptions"
            :placeholder="t('maintenance_create.header.actions.placeholder.priority')"
            :label-attribute="'name'"
            @option-selected="handlePrioritySelected" />
          <p v-if="errors.priority" class="mt-1 text-sm text-red-500">
            {{ errors.priority }}
          </p>
        </div>
      </div>
    </div>

    <div>
      <h2 class="mb-4 border-b border-gray-300 pb-3 text-lg font-semibold">{{ t('maintenance_create.body.subtitle_active_info') }}</h2>
      <div class="flex flex-wrap gap-4">
        <div v-if="isSuperAdmin" class="w-full md:w-[48%]">
          <label class="mb-2.5 block font-medium text-black">{{ t('maintenance_create.body.form.company') }}</label>
          <Dropdown
            name="company"
            :already-selected-option="selectedCompanyForDropdown"
            :label="t('maintenance_create.body.form.company')"
            :options="companies"
            :placeholder="t('maintenance_create.header.actions.placeholder.company')"
            :label-attribute="'name'"
            @option-selected="handleCompanySelected" />
          <p v-if="errors.company_id" class="mt-1 text-sm text-red-500">
            {{ errors.company_id }}
          </p>
        </div>
        <div class="w-full md:w-[48%]">
          <label class="mb-2.5 block font-medium text-black">{{ t('maintenance_create.body.form.sensor') }}</label>
          <DropdownSensors
            v-model="formData.sensor_id"
            name="sensor"
            :label="t('maintenance_listing.body.table.columns.sensor')"
            :options="sensorOptions"
            :placeholder="t('maintenance_create.header.actions.placeholder.sensor')"
            :label-attribute="'name'"
            @option-selected="handleSensorSelected" />
          <p v-if="errors.sensor_id" class="mt-1 text-sm text-red-500">
            {{ errors.sensor_id }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="showAllocationSection" class="space-y-4">
      <h2 class="mb-4 border-b border-gray-300 pb-3 text-lg font-semibold">Informações de alocação</h2>
      <div class="flex flex-wrap gap-4">
        <div v-if="isSuperAdmin" class="w-full md:w-[48%]">
          <label class="mb-2.5 block font-medium text-black">{{ t('maintenance_create.body.form.allocate_to_company') }}</label>
          <Dropdown
            name="allocation_company"
            :already-selected-option="selectedAllocationCompanyForDropdown"
            :label="t('maintenance_create.body.form.allocate_to_company')"
            :options="companies"
            :placeholder="t('maintenance_create.header.actions.placeholder.company')"
            :label-attribute="'name'"
            @option-selected="handleAllocationCompanySelected" />
        </div>
        <div class="w-full md:w-[48%]">
          <label class="mb-2.5 block font-medium text-black">Técnico responsável</label>
          <Dropdown
            name="allocated_user"
            :already-selected-option="selectedAllocatedUserForDropdown"
            :label="'Técnico responsável'"
            :options="allocationUserOptions"
            :placeholder="'Selecione o técnico'"
            :label-attribute="'name'"
            @option-selected="handleAllocatedUserSelected" />
          <p v-if="errors.allocated_user_id" class="mt-1 text-sm text-red-500">
            {{ errors.allocated_user_id }}
          </p>
        </div>
      </div>
    </div>

    <div>
      <h2 class="mb-4 border-b border-gray-300 pb-3 text-lg font-semibold">{{ t('maintenance_create.body.subtitle_details') }}</h2>
      <div class="flex flex-wrap gap-4">
        <div class="flex w-full items-end md:w-[48%]">
          <label class="flex items-center gap-2">
            <input v-model="formData.requires_shutdown" type="checkbox" class="h-5 w-5 border border-stroke bg-white text-primary" />
            <span class="text-black">{{ t('maintenance_create.body.form.requires_shutdown') }}</span>
          </label>
        </div>

        <div class="w-full">
          <label class="mb-2.5 block font-medium text-black">{{ t('maintenance_create.body.form.description') }}</label>
          <textarea
            v-model="formData.description"
            rows="5"
            class="w-full border border-stroke bg-white px-4 py-3 text-black"
            :placeholder="t('maintenance_listing.header.actions.placeholder.description')" />
          <p v-if="errors.description" class="mt-1 text-sm text-red-500">
            {{ errors.description }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-4">
      <button type="button" @click="cancel" class="border border-stroke px-6 py-2 font-medium text-black hover:shadow">Cancelar</button>
      <button type="submit" class="bg-primary px-6 py-2 font-medium text-white hover:bg-opacity-90">
        <span v-if="isLoading">
          <svg class="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0..." />
          </svg>
        </span>
        <span v-else>Salvar</span>
      </button>
    </div>
  </form>
</template>
